import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { makeStyles } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { getCallingBoard, getInvoice } from "apis/orderAPIs";
import { BUCKET_READY, KIND_ORDER, KIND_PAGER } from "util/orderStatus";

// The customer's phone, not a wall display, but the same dark palette as the
// board it mirrors - the two screens name the same number and should look like
// two sizes of the same thing.
const INK = "#0B0F14";
const INK_RAISED = "#141A22";
const HAIRLINE = "rgba(255, 255, 255, 0.08)";
const MUTED = "rgba(255, 255, 255, 0.55)";
const PREPARING_INK = "#FFB020";
const READY_INK = "#3DDC84";

const REFRESH_MS = 5000;
const RETRIES = 2;

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: INK,
    color: theme.palette.white,
    fontFamily: theme.typography.fontFamily,
  },
  header: {
    padding: theme.spacing(2.5, 3),
    borderBottom: `1px solid ${HAIRLINE}`,
  },
  venue: {
    margin: 0,
    fontSize: "1.1rem",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(3),
    padding: theme.spacing(4, 3),
    textAlign: "center",
  },
  numberCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(3, 5),
    borderRadius: 18,
    backgroundColor: INK_RAISED,
    border: `1px solid ${HAIRLINE}`,
    minWidth: "min(70vw, 20rem)",
  },
  numberKind: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: MUTED,
  },
  number: {
    fontSize: "clamp(3rem, 18vw, 5rem)",
    fontWeight: 700,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  statusReady: { color: READY_INK },
  statusPreparing: { color: PREPARING_INK },
  statusLabel: {
    margin: 0,
    fontSize: "1.6rem",
    fontWeight: 700,
  },
  statusDetail: {
    margin: 0,
    maxWidth: "26rem",
    fontSize: "1rem",
    lineHeight: 1.5,
    color: MUTED,
  },
  liveDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    marginRight: theme.spacing(1),
    borderRadius: "50%",
    backgroundColor: PREPARING_INK,
    animation: "$pulse 1.6s ease-in-out infinite",
  },
  liveDotReady: {
    backgroundColor: READY_INK,
    animation: "none",
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 0.35 },
    "50%": { opacity: 1 },
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(3),
    borderTop: `1px solid ${HAIRLINE}`,
  },
  hint: {
    margin: 0,
    fontSize: "0.8rem",
    color: MUTED,
  },
  receiptLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.white,
    fontWeight: 600,
    textDecoration: "underline",
  },
  banner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.warning.dark,
    color: theme.palette.white,
    fontWeight: 600,
    fontSize: "0.85rem",
  },
}));

/**
 * What this order's board entry says right now, remembering what it said before.
 *
 * The board only carries live entries, so "not there" is ambiguous: an order that
 * was never seen may be dine-in or long gone, while one that was on the board a
 * moment ago has just been collected. The page watches, so it can tell the two
 * apart - that is the whole reason this hook keeps state.
 */
const useOrderPhase = (entry, isSettled) => {
  const seenRef = useRef(null);
  const [phase, setPhase] = useState("checking");

  useEffect(() => {
    if (!isSettled) {
      return;
    }
    if (entry) {
      seenRef.current = entry.bucket;
      setPhase(entry.bucket === BUCKET_READY ? "ready" : "preparing");
      return;
    }
    setPhase(seenRef.current ? "collected" : "unknown");
  }, [entry, isSettled]);

  return phase;
};

const PHASE_COPY = {
  checking: {
    label: "Checking your order…",
    detail: "One moment while we find your order on the board.",
  },
  preparing: {
    label: "Being prepared",
    detail:
      "We're making your order now. This page updates by itself — we'll show you the moment it's ready.",
  },
  ready: {
    label: "Ready for pickup!",
    detail: "Your order is ready — please collect it from the counter.",
  },
  collected: {
    label: "Order collected",
    detail: "Your order has been handed over. Enjoy!",
  },
  unknown: {
    label: "Not on the board",
    detail:
      "We can't see this order on the calling board. If you ordered for dine-in we'll bring it to you; otherwise our staff can check it for you.",
  },
};

/**
 * Public single-order status page - the calling board, scoped to one customer.
 *
 * Reached from the QR code on the kiosk's order-success screen, so it must work
 * with nothing but the URL: no login, no app state. It reads the same public
 * board endpoint as the in-store display, which returns order numbers only, and
 * simply looks for this customer's number in the payload.
 */
const OrderStatus = (props) => {
  const classes = useStyles();
  const { restaurantId, orderId } = props.match.params;
  const pagerNumber = (
    new URLSearchParams(props.location.search).get("pager") || ""
  ).trim();

  // The same last-4 derivation as the kiosk receipt and the board, so all three
  // surfaces name the number identically.
  const orderRef = String(orderId || "").slice(-4).toUpperCase();

  const { data, isError, isFetched } = useQuery({
    queryKey: ["orderStatus", restaurantId],
    queryFn: () => getCallingBoard(restaurantId),
    enabled: Boolean(restaurantId),
    refetchInterval: REFRESH_MS,
    // The phone sits in a pocket with the screen on this page; keep polling so
    // it is current the moment the customer looks again.
    refetchIntervalInBackground: true,
    placeholderData: (previous) => previous,
    retry: RETRIES,
    gcTime: Infinity,
  });

  // Receipts exist only for orders paid at the kiosk, and asking is the only way
  // to know - a plain 400 means "no receipt", so never retry or poll for it.
  const { data: invoice } = useQuery({
    queryKey: ["orderStatusInvoice", restaurantId, orderId],
    queryFn: () => getInvoice(restaurantId, orderId),
    enabled: Boolean(restaurantId && orderId),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const orders = (data && data.orders) || [];
  // A kiosk order with a pager is called by its pager number, but fall back to
  // the order reference so the page still works if the pager never reached the
  // board (an older server, a POS that dropped it).
  const entry =
    (pagerNumber &&
      orders.find(
        (candidate) =>
          candidate.kind === KIND_PAGER && candidate.ref === pagerNumber
      )) ||
    orders.find(
      (candidate) => candidate.kind === KIND_ORDER && candidate.ref === orderRef
    ) ||
    null;

  const phase = useOrderPhase(entry, isFetched && Boolean(data));
  const copy = PHASE_COPY[phase];
  const isReady = phase === "ready";

  const venue =
    (data && data.restaurant && (data.restaurant.name || data.restaurant.altName)) ||
    "";

  // The board endpoint answered at least once and then stopped: keep the last
  // known state on screen but say it may be stale, exactly as the board does.
  const isStale = isError && Boolean(data);

  if (isError && !data) {
    return (
      <div className={classes.root}>
        <div className={classes.body}>
          <WifiOffIcon fontSize="large" />
          <p className={classes.statusLabel}>Can&apos;t check right now</p>
          <p className={classes.statusDetail}>
            We couldn&apos;t reach the order board. This page retries by itself —
            or ask our staff about your order.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {isStale && (
        <div className={classes.banner}>
          <WifiOffIcon fontSize="small" />
          Reconnecting — status may be out of date
        </div>
      )}

      <header className={classes.header}>
        <h1 className={classes.venue}>{venue || "Your order"}</h1>
      </header>

      <main className={classes.body}>
        <div className={classes.numberCard}>
          <span className={classes.numberKind}>
            {pagerNumber ? "Pager number" : "Order number"}
          </span>
          <span
            className={`${classes.number} ${
              isReady
                ? classes.statusReady
                : phase === "preparing"
                  ? classes.statusPreparing
                  : ""
            }`}
          >
            {pagerNumber || orderRef}
          </span>
        </div>

        {phase === "checking" ? (
          <CircularProgress color="inherit" size={32} />
        ) : (
          <p
            className={`${classes.statusLabel} ${
              isReady
                ? classes.statusReady
                : phase === "preparing"
                  ? classes.statusPreparing
                  : ""
            }`}
          >
            {(phase === "preparing" || isReady) && (
              <span
                className={`${classes.liveDot} ${isReady ? classes.liveDotReady : ""}`}
              />
            )}
            {copy.label}
          </p>
        )}
        <p className={classes.statusDetail}>{copy.detail}</p>
      </main>

      <footer className={classes.footer}>
        {invoice && (
          <a
            className={classes.receiptLink}
            href={`/invoice/${restaurantId}/${orderId}`}
          >
            <ReceiptLongIcon fontSize="small" />
            View your receipt
          </a>
        )}
        <p className={classes.hint}>This page updates automatically.</p>
      </footer>
    </div>
  );
};

OrderStatus.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      restaurantId: PropTypes.string,
      orderId: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

export default OrderStatus;
